import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Select,
  Typography,
  Tag,
  message,
  Space,
  Alert,
  Spin,
  InputNumber,
  Table,
  Progress,
} from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { aiSettingsAPI } from '../services/api.ts';
import DocumentImportRegistry from '../components/DocumentImportRegistry.tsx';
import { AIBudgetStatus } from '../types.ts';

const { Title, Text } = Typography;

interface ProviderState {
  configured: boolean;
}

interface AISettingsData {
  providers: Record<string, ProviderState>;
  active_provider: string | null;
  active_model: string | null;
  monthly_budget_usd?: number | null;
  model_prices?: Record<string, { input: number; output: number }>;
  default_prices?: { prefix: string; input: number; output: number }[];
  budget?: AIBudgetStatus;
}

interface ModelOption {
  id: string;
  display_name: string;
}

const PROVIDER_INFO: Record<string, { label: string; keyField: string; placeholder: string }> = {
  anthropic: { label: 'Anthropic (Claude)', keyField: 'anthropic_api_key', placeholder: 'sk-ant-...' },
  openai: { label: 'OpenAI (ChatGPT)', keyField: 'openai_api_key', placeholder: 'sk-...' },
  google: { label: 'Google (Gemini)', keyField: 'google_api_key', placeholder: 'AIza...' },
};

const AISettings: React.FC = () => {
  const [settings, setSettings] = useState<AISettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});

  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(undefined);
  const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [savingActive, setSavingActive] = useState(false);

  // Costi e budget (v3.1.0): budget mensile in USD e tariffe per modello (USD / 1M token)
  const [budgetInput, setBudgetInput] = useState<number | null>(null);
  const [priceRows, setPriceRows] = useState<{ model: string; input: number; output: number }[]>([]);
  const [savingCosts, setSavingCosts] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await aiSettingsAPI.getSettings();
      setSettings(data);
      setBudgetInput(data.monthly_budget_usd ?? null);
      setPriceRows(Object.entries(data.model_prices || {}).map(([model, p]: any) => ({ model, input: p.input, output: p.output })));
      setSelectedProvider(data.active_provider || undefined);
      setSelectedModel(data.active_model || undefined);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveKey = async (provider: string) => {
    const value = keyInputs[provider];
    if (value === undefined || value.trim() === '') {
      message.warning('Inserire una API key da salvare');
      return;
    }
    setSavingKey(provider);
    try {
      const data = await aiSettingsAPI.updateSettings({
        [PROVIDER_INFO[provider].keyField]: value.trim(),
      });
      setSettings(data);
      setKeyInputs(prev => ({ ...prev, [provider]: '' }));
      message.success(`API key di ${PROVIDER_INFO[provider].label} salvata`);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel salvataggio della chiave');
    } finally {
      setSavingKey(null);
    }
  };

  const handleLoadModels = async (provider?: string) => {
    const target = provider || selectedProvider;
    if (!target) {
      message.warning('Selezionare prima un provider');
      return;
    }
    if (!settings?.providers?.[target]?.configured) {
      message.warning(`Configurare prima la API key di ${PROVIDER_INFO[target].label}`);
      return;
    }
    setLoadingModels(true);
    setModels([]);
    try {
      const data = await aiSettingsAPI.listModels(target);
      setModels(data.models || []);
      if (!data.models?.length) {
        message.warning('Nessun modello restituito dal provider');
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel recupero dei modelli');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleProviderChange = (provider: string) => {
    setSelectedProvider(provider);
    setSelectedModel(undefined);
    setModels([]);
    if (settings?.providers?.[provider]?.configured) {
      handleLoadModels(provider);
    }
  };

  const handleSaveActive = async () => {
    if (!selectedProvider || !selectedModel) {
      message.warning('Selezionare provider e modello');
      return;
    }
    setSavingActive(true);
    try {
      const data = await aiSettingsAPI.updateSettings({
        active_provider: selectedProvider,
        active_model: selectedModel,
      });
      setSettings(data);
      message.success(`Modello attivo: ${selectedModel}`);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel salvataggio della configurazione');
    } finally {
      setSavingActive(false);
    }
  };

  if (loading || !settings) {
    return <Spin style={{ display: 'block', margin: '80px auto' }} size="large" />;
  }

  const activeInfo = settings.active_provider && settings.active_model
    ? `${PROVIDER_INFO[settings.active_provider]?.label || settings.active_provider} — ${settings.active_model}`
    : null;

  const handleSaveCosts = async () => {
    setSavingCosts(true);
    try {
      const model_prices: Record<string, { input: number; output: number }> = {};
      priceRows.forEach(r => { if (r.model.trim()) model_prices[r.model.trim()] = { input: r.input || 0, output: r.output || 0 }; });
      const data = await aiSettingsAPI.updateSettings({ monthly_budget_usd: budgetInput ?? 0, model_prices });
      setSettings(data);
      message.success('Costi e budget salvati');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingCosts(false);
    }
  };

  const budget = settings?.budget;

  return (
    <div className="no-uppercase-modal">
      <Title level={2}>
        <ApiOutlined style={{ marginRight: 8 }} />
        Impostazioni API — Modelli AI
      </Title>
      <Text type="secondary">
        Configura le credenziali dei provider e scegli il modello usato da "Carica Ordine Cliente con AI".
        Le chiavi sono salvate cifrate e non vengono mai rivisualizzate.
      </Text>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {Object.entries(PROVIDER_INFO).map(([provider, info]) => (
          <Col xs={24} lg={8} key={provider}>
            <Card
              size="small"
              title={info.label}
              extra={
                settings.providers[provider]?.configured
                  ? <Tag icon={<CheckCircleOutlined />} color="green">configurata</Tag>
                  : <Tag color="default">non configurata</Tag>
              }
            >
              <Space.Compact style={{ width: '100%' }}>
                <Input.Password
                  placeholder={settings.providers[provider]?.configured ? '••••••••  (sostituisci)' : info.placeholder}
                  value={keyInputs[provider] || ''}
                  onChange={(e) => setKeyInputs(prev => ({ ...prev, [provider]: e.target.value }))}
                  autoComplete="new-password"
                />
                <Button
                  type="primary"
                  loading={savingKey === provider}
                  onClick={() => handleSaveKey(provider)}
                >
                  Salva
                </Button>
              </Space.Compact>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 24 }} title="Modello attivo">
        {activeInfo ? (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={<span>Modello attualmente in uso: <b>{activeInfo}</b></span>}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Nessun modello configurato: la funzione 'Carica Ordine Cliente con AI' non è disponibile per gli utenti."
          />
        )}

        <Space wrap>
          <Select
            style={{ width: 220 }}
            placeholder="Provider"
            value={selectedProvider}
            onChange={handleProviderChange}
            options={Object.entries(PROVIDER_INFO).map(([value, info]) => ({
              value,
              label: info.label,
              disabled: !settings.providers[value]?.configured,
            }))}
          />
          <Select
            style={{ width: 360 }}
            placeholder={loadingModels ? 'Caricamento modelli…' : 'Modello'}
            value={selectedModel}
            onChange={setSelectedModel}
            loading={loadingModels}
            showSearch
            optionFilterProp="label"
            options={models.map(m => ({ value: m.id, label: `${m.display_name} (${m.id})` }))}
            notFoundContent={loadingModels ? <Spin size="small" /> : 'Carica i modelli del provider'}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => handleLoadModels()}
            loading={loadingModels}
          >
            Carica modelli
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveActive}
            loading={savingActive}
            disabled={!selectedProvider || !selectedModel}
          >
            Salva configurazione
          </Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 24 }} title="Costi e budget mensile">
        <Text type="secondary">
          Il saldo dell'account non è leggibile via API: il costo di ogni analisi viene stimato dai token
          della risposta per la tariffa del modello (USD per milione di token) e sommato nel mese. Raggiunto il
          budget, le analisi vengono rifiutate prima della chiamata. Le tariffe predefinite sono indicative:
          verificale sul listino del provider e correggile qui.
        </Text>
        {budget && (
          <div style={{ marginTop: 16, maxWidth: 520 }}>
            <Text>
              Speso questo mese: <b>{budget.spent_usd.toFixed(2)} $</b>
              {budget.budget_usd ? <> su <b>{budget.budget_usd.toFixed(2)} $</b></> : <> (nessun budget impostato)</>}
            </Text>
            {budget.budget_usd ? (
              <Progress
                percent={Math.min(100, budget.percent || 0)}
                status={budget.blocked ? 'exception' : budget.warning ? 'active' : 'normal'}
                strokeColor={budget.blocked ? '#cf1322' : budget.warning ? '#fa8c16' : undefined}
              />
            ) : null}
            {budget.blocked && <Alert type="error" showIcon message="Budget esaurito: le analisi con AI sono bloccate fino al mese prossimo o all'aumento del budget." />}
            {budget.warning && <Alert type="warning" showIcon message="Superato l'80% del budget mensile." />}
          </div>
        )}
        <Space wrap style={{ marginTop: 16 }} align="start">
          <div>
            <div><Text strong>Budget mensile (USD)</Text></div>
            <InputNumber
              min={0}
              step={10}
              precision={2}
              value={budgetInput ?? undefined}
              onChange={(v) => setBudgetInput(v ?? null)}
              placeholder="0 = nessun limite"
              style={{ width: 200 }}
            />
          </div>
        </Space>
        <div style={{ marginTop: 16 }}>
          <Text strong>Tariffe per modello (USD per 1M token)</Text>
          <Table
            size="small"
            style={{ marginTop: 8, maxWidth: 760 }}
            rowKey={(r, i) => `${i}`}
            pagination={false}
            dataSource={priceRows}
            columns={[
              {
                title: 'Modello (id)', dataIndex: 'model',
                render: (v: string, r, i: number) => (
                  <Input value={v} placeholder="es. claude-sonnet-4-6" onChange={(e) => setPriceRows(rows => rows.map((x, k) => k === i ? { ...x, model: e.target.value } : x))} />
                ),
              },
              {
                title: 'Input', dataIndex: 'input', width: 140,
                render: (v: number, r, i: number) => (
                  <InputNumber min={0} step={0.01} value={v} onChange={(val) => setPriceRows(rows => rows.map((x, k) => k === i ? { ...x, input: val || 0 } : x))} />
                ),
              },
              {
                title: 'Output', dataIndex: 'output', width: 140,
                render: (v: number, r, i: number) => (
                  <InputNumber min={0} step={0.01} value={v} onChange={(val) => setPriceRows(rows => rows.map((x, k) => k === i ? { ...x, output: val || 0 } : x))} />
                ),
              },
              {
                title: '', width: 90,
                render: (_: any, r, i: number) => (
                  <Button size="small" danger onClick={() => setPriceRows(rows => rows.filter((_, k) => k !== i))}>Rimuovi</Button>
                ),
              },
            ]}
          />
          <Space style={{ marginTop: 8 }}>
            <Button onClick={() => setPriceRows(rows => [...rows, { model: settings.active_model || '', input: 0, output: 0 }])}>
              Aggiungi tariffa
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={savingCosts} onClick={handleSaveCosts}>
              Salva costi e budget
            </Button>
          </Space>
          {settings.default_prices && settings.default_prices.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Predefinite (per prefisso, se il modello non ha una tariffa propria):{' '}
                {settings.default_prices.map(d => `${d.prefix} ${d.input}/${d.output}`).join(' · ')}
              </Text>
            </div>
          )}
        </div>
      </Card>

      <Card style={{ marginTop: 24 }} title="Registro importazioni da documento">
        <Text type="secondary">
          Ogni analisi con AI lascia una riga: file, modello, token, costo stimato ed esito. Espandi una riga per
          vedere i prompt inviati (il documento non viene salvato) e il JSON restituito dal modello.
        </Text>
        <div style={{ marginTop: 16 }}>
          <DocumentImportRegistry />
        </div>
      </Card>
    </div>
  );
};

export default AISettings;
